/**
 * Custom hooks for Document Access Tokens
 * Handles creating and managing access tokens for external users
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface DocumentAccessToken {
  suggestion_access_id: string;
  document_id: string;
  company_id: string;
  granted_to_email: string;
  granted_by: string;
  access_token: string;
  status: 'active' | 'expired' | 'revoked';
  token_expires_at: string | null;
  can_view: boolean;
  can_comment: boolean;
  can_suggest: boolean;
  can_resolve: boolean;
  access_count: number;
  last_accessed_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  created_at: string;
}

export interface CreateAccessTokenRequest {
  email: string;
  expires_in_hours?: number;
  can_view?: boolean;
  can_comment?: boolean;
  can_suggest?: boolean;
  can_resolve?: boolean;
  notes?: string;
}

export interface PublicDocumentInfo {
  document_id: string;
  file_name: string;
  document_type: string | null;
  page_count: number | null;
  container_id: string | null;
  permissions: {
    can_view: boolean;
    can_comment: boolean;
    can_suggest: boolean;
    can_resolve: boolean;
  };
  pending_issues_count: number;
  all_issues_resolved: boolean;
}

export interface PublicDownloadInfo {
  download_url: string;
  file_name: string;
  file_size_bytes: number | null;
  content_type: string | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const ACCESS_TOKEN_QUERY_KEYS = {
  all: ['document-access-tokens'] as const,
  byDocument: (documentId: string) =>
    [...ACCESS_TOKEN_QUERY_KEYS.all, documentId] as const,
  publicDocument: (token: string) =>
    ['public-document', token] as const,
};

// ============================================================================
// Authenticated Hooks (for document owners)
// ============================================================================

/**
 * Fetch all access tokens for a document
 */
export function useDocumentAccessTokens(documentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ACCESS_TOKEN_QUERY_KEYS.byDocument(documentId || ''),
    queryFn: async (): Promise<DocumentAccessToken[]> => {
      return await apiGet<DocumentAccessToken[]>(
        `/documents/${documentId}/access`
      );
    },
    enabled: enabled && !!documentId,
  });
}

/**
 * Create a new access token for a document
 */
export function useCreateAccessToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      ...data
    }: CreateAccessTokenRequest & { documentId: string }): Promise<DocumentAccessToken> => {
      return await apiPost<DocumentAccessToken>(
        `/documents/${documentId}/access`,
        data
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ACCESS_TOKEN_QUERY_KEYS.byDocument(result.document_id),
      });
    },
  });
}

/**
 * Revoke an access token
 */
export function useRevokeAccessToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tokenId,
      documentId: _documentId,
    }: {
      tokenId: string;
      documentId: string;
    }): Promise<void> => {
      await apiPatch(`/access-tokens/${tokenId}/revoke`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ACCESS_TOKEN_QUERY_KEYS.byDocument(variables.documentId),
      });
    },
  });
}

// ============================================================================
// Public Hooks (for external users with access token)
// ============================================================================

/**
 * Validate a public access token and get document info
 */
export function usePublicDocumentByToken(token: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ACCESS_TOKEN_QUERY_KEYS.publicDocument(token || ''),
    queryFn: async (): Promise<PublicDocumentInfo> => {
      return await apiGet<PublicDocumentInfo>(
        `/public/documents/${token}`
      );
    },
    enabled: enabled && !!token,
    // Don't retry on 403/404 (invalid/expired token)
    retry: (failureCount, error: { status?: number }) => {
      if (error?.status === 403 || error?.status === 404) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Get suggestions for a document via public token
 */
export function usePublicDocumentSuggestions(token: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ACCESS_TOKEN_QUERY_KEYS.publicDocument(token || ''), 'suggestions'],
    queryFn: async () => {
      return await apiGet(`/public/documents/${token}/suggestions`);
    },
    enabled: enabled && !!token,
  });
}

/**
 * Send a chat message via public token
 */
export function usePublicSendChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      suggestionId,
      message,
      senderName,
    }: {
      token: string;
      suggestionId: string;
      message: string;
      senderName?: string;
    }) => {
      return await apiPost(`/public/documents/${token}/chat`, {
        suggestion_id: suggestionId,
        message,
        sender_name: senderName,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...ACCESS_TOKEN_QUERY_KEYS.publicDocument(variables.token), 'suggestions'],
      });
    },
  });
}

/**
 * Get download URL for document via public token
 */
export function usePublicDocumentDownload(token: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ACCESS_TOKEN_QUERY_KEYS.publicDocument(token || ''), 'download'],
    queryFn: async (): Promise<PublicDownloadInfo> => {
      return await apiGet<PublicDownloadInfo>(
        `/public/documents/${token}/download`
      );
    },
    enabled: enabled && !!token,
    // Cache for 5 minutes (URL is valid for 1 hour)
    staleTime: 5 * 60 * 1000,
  });
}
